import { useLayoutEffect, useRef, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button-v25-3/new';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';
import { Flex, Inline, Stack } from '@atlaskit/primitives';
import { Text } from '@atlaskit/primitives-next';
import { token } from '@atlaskit/tokens';

import { type ArticleAccentColor, type PortalArticle } from '../../jsm-portal-playground-data';
import { jsmPortalPlaygroundMessages as messages } from '../../jsm-portal-playground-messages';
import { useJsmPlaygroundAnalytics } from '../../use-jsm-playground-analytics';
import { JsmPlaygroundDemoCursor } from '../jsm-playground-demo-cursor';
import { centerOf, rectWithin } from '../jsm-playground-demo-geometry';
import { useJsmPlaygroundDemoLayer } from '../jsm-playground-demo-layer';
import { useDemoCursor, useDemoTimeline } from '../jsm-playground-demo-timeline';
import { JsmPlaygroundHeading } from '../jsm-playground-heading';

import { ARTICLE_DEMO, DEMO_CURSOR_LABEL } from './jsm-portal-playground-it-support-demo';
import {
	ARTICLE_DATE_FORMAT,
	BEACON_GRADIENT,
	BRAND_FONT,
	PortalArticleIconTile,
} from './jsm-portal-playground-it-support-shared';
import {
	JsmPortalPlaygroundRovoLauncher,
	usePortalRovoChat,
} from './jsm-portal-playground-rovo-chat';

export const JsmPortalPlaygroundArticleView = ({
	article,
	secondaryColor,
	onBack,
	shouldPlayDemo = false,
	hasUserTakenOver = false,
	shouldCompleteChat = false,
	onDemoComplete,
}: {
	article: PortalArticle;
	secondaryColor: ArticleAccentColor;
	onBack: () => void;
	shouldPlayDemo?: boolean;
	hasUserTakenOver?: boolean;
	shouldCompleteChat?: boolean;
	onDemoComplete?: () => void;
}) => {
	const intl = useIntl();
	const chat = usePortalRovoChat();
	const pageRef = useRef<HTMLDivElement>(null);
	const askPillRef = useRef<HTMLButtonElement>(null);
	const demoLayer = useJsmPlaygroundDemoLayer();
	const demoCursor = useDemoCursor<'start' | 'askPill'>('start');
	const { updateCursor, updateStops } = demoCursor;
	const [demoPillState, setDemoPillState] = useState<'idle' | 'hovered' | 'pressed'>('idle');
	const { firePlaygroundScreenNavigated } = useJsmPlaygroundAnalytics('it_support');

	const handleBack = () => {
		firePlaygroundScreenNavigated({
			team_type: 'it_support',
			screen_id: 'help_center_hub',
			interaction_mode: 'manual_click',
		});
		onBack();
	};

	useLayoutEffect(() => {
		const origin = demoLayer ?? pageRef.current;
		const pill = askPillRef.current;
		if (!shouldPlayDemo || !origin || !pill) {
			return;
		}
		const box = rectWithin(origin, pill);
		updateStops({ start: { x: box.left - 72, y: box.top - 104 }, askPill: centerOf(box) });
	}, [shouldPlayDemo, demoLayer, updateStops]);

	useLayoutEffect(() => {
		if (!shouldCompleteChat) {
			return;
		}
		updateCursor({ isVisible: false, isPressed: false });
		setDemoPillState('idle');
		chat.completeNow();
	}, [chat.completeNow, shouldCompleteChat, updateCursor]);

	useDemoTimeline(shouldPlayDemo && !shouldCompleteChat, hasUserTakenOver, (at) => {
		const { updateCursor } = demoCursor;

		at(ARTICLE_DEMO.cursorIn, () => {
			updateCursor({ target: 'askPill', isVisible: true });
			setDemoPillState('hovered');
		});
		at(ARTICLE_DEMO.askPillPress, () => {
			updateCursor({ isPressed: true });
			setDemoPillState('pressed');
			chat.open(askPillRef, { isDemo: true });
		});
		at(ARTICLE_DEMO.askPillRelease, () => {
			updateCursor({ isPressed: false });
			setDemoPillState('idle');
		});
		at(ARTICLE_DEMO.cursorOut, () => updateCursor({ isVisible: false }));
	});

	return (
		<div ref={pageRef} css={pageStyles} data-testid="jsm-portal-article-page">
			<div css={columnStyles}>
				<button
					type="button"
					css={backLinkStyles}
					aria-label={intl.formatMessage(messages.articleBackLabel)}
					data-testid="jsm-portal-article-back-button"
					onClick={handleBack}
				>
					<ArrowLeftIcon label="" size="small" color={token('color.text')} />
					<Text size="medium" weight="medium">
						{intl.formatMessage(messages.articleBack)}
					</Text>
				</button>

				<Stack space="space.400">
					<Flex alignItems="center" justifyContent="space-between" css={metaRowStyles}>
						<PortalArticleIconTile color={secondaryColor} size="medium" />
						<Inline space="space.075" alignBlock="center">
							<Text size="large" color="color.text.subtlest">
								{intl.formatMessage(messages.lastUpdated, {
									date: intl.formatDate(article.lastUpdated, ARTICLE_DATE_FORMAT),
								})}
							</Text>
							<span
								aria-hidden="true"
								// @ts-expect-error -- inert is missing from @compiled/react types
								inert=""
							>
								<Button
									iconBefore={() => <AiGenerativeTextIcon label="" />}
									spacing="compact"
									tabIndex={-1}
								>
									{intl.formatMessage(messages.draftWithRovo)}
								</Button>
							</span>
						</Inline>
					</Flex>

					<Stack space="space.150">
						<div aria-level={1} css={articleTitleStyles} role="heading">
							{intl.formatMessage(article.title)}
						</div>
						<Text size="large">{intl.formatMessage(article.intro)}</Text>
					</Stack>
				</Stack>

				<Stack space="space.300">
					{article.sections.map((section) => (
						<Stack key={section.heading.id} space="space.150">
							<JsmPlaygroundHeading level={2} size="medium">
								{intl.formatMessage(section.heading)}
							</JsmPlaygroundHeading>
							<Stack space="space.300">
								{section.paragraphs.map((lines, paragraphIndex) => (
									<Stack key={paragraphIndex} space="space.150" aria-hidden="true">
										{lines.map((width, lineIndex) => (
											<div
												key={lineIndex}
												css={redactedLineStyles}
												style={{ width: `${width * 100}%` }}
											/>
										))}
									</Stack>
								))}
							</Stack>
						</Stack>
					))}
				</Stack>
			</div>

			<div css={stillNeedHelpBandStyles}>
				<div css={stillNeedHelpCardStyles}>
					<Stack space="space.200" alignInline="center">
						<Stack space="space.100" alignInline="center">
							<div aria-level={2} css={stillNeedHelpTitleStyles} role="heading">
								{intl.formatMessage(messages.stillNeedHelpTitle)}
							</div>
							<p css={stillNeedHelpBodyStyles}>{intl.formatMessage(messages.stillNeedHelpBody)}</p>
						</Stack>

						<button
							ref={askPillRef}
							type="button"
							css={[
								askPillStyles,
								demoPillState === 'hovered' && askPillHoveredStyles,
								demoPillState === 'pressed' && askPillPressedStyles,
							]}
							onClick={() => chat.open(askPillRef)}
						>
							<AiChatIcon label="" color={token('color.text.inverse')} />
							<Text weight="medium" color="color.text.inverse">
								{intl.formatMessage(messages.askAnything)}
							</Text>
							<span css={askPillActionStyles} aria-hidden="true">
								<ArrowRightIcon label="" color={token('color.text')} />
							</span>
						</button>

						<Text weight="medium">{intl.formatMessage(messages.submitRequest)}</Text>
					</Stack>
				</div>
			</div>

			<JsmPortalPlaygroundRovoLauncher
				chat={chat}
				articleId={article.id}
				onStreamComplete={shouldPlayDemo ? onDemoComplete : undefined}
			/>

			{shouldPlayDemo && (
				<JsmPlaygroundDemoCursor
					label={DEMO_CURSOR_LABEL}
					testId="jsm-portal-article-demo-cursor"
					container={demoLayer}
					hasUserTakenOver={hasUserTakenOver}
					isPressed={demoCursor.isPressed}
					isTransitionEnabled={demoCursor.isTransitionEnabled}
					isVisible={demoCursor.isVisible}
					x={demoCursor.position.x}
					y={demoCursor.position.y}
				/>
			)}
		</div>
	);
};

const pageStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	position: 'relative',
	boxSizing: 'border-box',
	width: '100%',
	height: '100%',
	minHeight: 0,
	paddingBlockStart: token('space.200'),
	paddingBlockEnd: token('space.100'),
	paddingInline: token('space.500'),
	backgroundColor: token('elevation.surface.sunken'),
	overflow: 'hidden',
});

const columnStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignSelf: 'center',
	gap: token('space.250'),
	boxSizing: 'border-box',
	width: '878px',
	maxWidth: '100%',
});

const backLinkStyles = css({
	alignItems: 'center',
	alignSelf: 'flex-start',
	display: 'inline-flex',
	gap: token('space.050'),
	paddingBlock: token('space.025'),
	paddingInline: token('space.050'),
	borderStyle: 'none',
	borderRadius: token('radius.small'),
	backgroundColor: token('color.background.neutral.subtle'),
	color: token('color.text'),
	cursor: 'pointer',
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
	'&:focus-visible': {
		outlineWidth: token('border.width.focused', '2px'),
		outlineStyle: 'solid',
		outlineColor: token('color.border.focused'),
		outlineOffset: token('space.025'),
	},
});

const metaRowStyles = css({
	width: '100%',
});

const articleTitleStyles = css({
	margin: 0,
	color: token('color.text'),
	fontFamily: token(
		'font.family.body',
		'"Atlassian Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
	),
	fontSize: '40px',
	fontWeight: token('font.weight.bold', '653'),
	lineHeight: 1.1,
	letterSpacing: '-0.01em',
});

const redactedLineStyles = css({
	height: '12px',
	borderRadius: token('radius.xsmall'),
	backgroundColor: token('color.skeleton'),
});

const stillNeedHelpBandStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	marginBlockStart: 'auto',
	boxSizing: 'border-box',
	width: '100%',
	padding: token('space.150'),
});

const stillNeedHelpCardStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	boxSizing: 'border-box',
	width: '100%',
	maxWidth: '1202px',
	paddingBlock: token('space.250'),
	paddingInline: token('space.800'),
	borderRadius: token('radius.large'),
	backgroundImage: BEACON_GRADIENT,
	overflow: 'hidden',
});

const stillNeedHelpTitleStyles = css({
	margin: 0,
	color: token('color.text'),
	fontFamily: BRAND_FONT,
	fontSize: '40px',
	fontWeight: 700,
	lineHeight: 1.17,
	textAlign: 'center',
});

const stillNeedHelpBodyStyles = css({
	margin: 0,
	maxWidth: '567px',
	color: token('color.text'),
	fontFamily: `${BRAND_FONT} !important`,
	fontSize: '16px !important',
	fontWeight: '400 !important',
	lineHeight: '1.25 !important',
	textAlign: 'center',
});

const askPillStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.100'),
	height: '60px',
	paddingBlock: token('space.150'),
	paddingInline: token('space.200'),
	borderStyle: 'none',
	borderRadius: token('radius.xxlarge'),
	cursor: 'pointer',
	backgroundColor: token('color.text'),
	transition: 'box-shadow 150ms ease, transform 150ms ease',
	'&:hover': {
		boxShadow: token('elevation.shadow.overlay'),
	},
	'&:active': {
		boxShadow: token('elevation.shadow.raised'),
		transform: 'translateY(1px)',
	},
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});

const askPillHoveredStyles = css({
	boxShadow: token('elevation.shadow.overlay'),
});

const askPillPressedStyles = css({
	boxShadow: token('elevation.shadow.raised'),
	transform: 'translateY(1px)',
});

const askPillActionStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	width: '34px',
	height: '34px',
	borderRadius: token('radius.full'),
	// eslint-disable-next-line local/ensure-wac-token-usage
	backgroundColor: '#B2EABB',
});
